import { FormControlLabel, Grid, Link, TextField, Typography, Autocomplete } from '@mui/material';

import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import InfoBox from 'app/components/InfoBox';
import { translations } from 'locales/i18n';
import { countries, countryToFlag, CountryType } from 'utils/Countries';

import ErrorMarker from '../components/ErrorMarker';

export default function InvoiceData() {
  const { errors, touched, values, setFieldValue }: any = useFormikContext<any>();
  const { t } = useTranslation();

  return (
    <>
      <Grid container className="register-form__description" justifyContent="center">
        <Grid container paddingRight={1} paddingTop={1} textAlign="center">
          <Typography>
            {t(translations.pages.login.formDescriptions.invoiceData())}
            <br />
          </Typography>
          <InfoBox
            type="warning"
            title={t(translations.pages.profile.summaryInfoBlockedDepartments.title())}
            collapsed
            expandable
          >
            <Typography paragraph={true} textAlign={'center'}>
              {t(translations.pages.login.formDescriptions.invoiceInfo())}
              <br />
              {t(translations.pages.pricingslots.linktext())}
              <Link href="/jobs/pricing" target="_blank">
                {t(translations.pages.pricingslots.actuallink())}
              </Link>
            </Typography>
          </InfoBox>
          {values.companyData.country === 'Germany' && (
            <Typography paddingTop={1}>
              {t(translations.pages.login.formDescriptions.requiredEitherUstIdOrTaxId())}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.taxId())}
            placeholder={t(translations.pages.login.formDescriptions.taxIdInfo())}
            name="invoiceData.taxId"
            as={TextField}
            error={touched.invoiceData?.taxId && errors.invoiceData?.taxId}
            variant="outlined"
            size="small"
            fullWidth
          />
        </Grid>
        {touched.invoiceData?.taxId && errors.invoiceData?.taxId && (
          <ErrorMarker message={errors.invoiceData?.taxId} />
        )}
        {values.companyData.country === 'Germany' && (
          <Grid item xs={11} md>
            <Field
              className="register-form__field"
              label={t(translations.common.taxNumber())}
              name="invoiceData.taxNumber"
              as={TextField}
              error={touched.invoiceData?.taxNumber && errors.invoiceData?.taxNumber}
              variant="outlined"
              size="small"
              fullWidth
              disabled={values.companyData.country !== 'Germany'}
            />
          </Grid>
        )}
        {touched.invoiceData?.taxNumber && errors.invoiceData?.taxNumber && (
          <ErrorMarker message={errors.invoiceData?.taxNumber} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.invoiceEmail())}
            name="invoiceData.invoiceEmail"
            as={TextField}
            error={touched.invoiceData?.invoiceEmail && errors.invoiceData?.invoiceEmail}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        {touched.invoiceData?.invoiceEmail && errors.invoiceData?.invoiceEmail && (
          <ErrorMarker message={errors.invoiceData?.invoiceEmail} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.companyName())}
            name="invoiceData.invoiceName"
            value={values.useSameAddress ? values.companyData.name : values.invoiceData.invoiceName}
            as={TextField}
            error={touched.invoiceData?.invoiceName && errors.invoiceData?.invoiceName}
            variant="outlined"
            size="small"
            fullWidth
            disabled={values.useSameAddress}
            required
          />
        </Grid>
        {touched.invoiceData?.invoiceName && errors.invoiceData?.invoiceName && (
          <ErrorMarker message={errors.invoiceData?.invoiceName} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.street())}
            name="invoiceData.invoiceStreet"
            value={
              values.useSameAddress ? values.companyData.street : values.invoiceData.invoiceStreet
            }
            as={TextField}
            error={touched.invoiceData?.invoiceStreet && errors.invoiceData?.invoiceStreet}
            variant="outlined"
            size="small"
            fullWidth
            disabled={values.useSameAddress}
            required
          />
        </Grid>
        {touched.invoiceData?.invoiceStreet && errors.invoiceData?.invoiceStreet && (
          <ErrorMarker message={errors.invoiceData?.invoiceStreet} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.zip())}
            name="invoiceData.invoiceZip"
            value={values.useSameAddress ? values.companyData.zip : values.invoiceData.invoiceZip}
            as={TextField}
            error={touched.invoiceData?.invoiceZip && errors.invoiceData?.invoiceZip}
            variant="outlined"
            size="small"
            fullWidth
            disabled={values.useSameAddress}
            required
          />
        </Grid>
        {touched.invoiceData?.invoiceZip && errors.invoiceData?.invoiceZip && (
          <ErrorMarker message={errors.invoiceData?.invoiceZip} />
        )}
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.city())}
            name="invoiceData.invoiceCity"
            value={values.useSameAddress ? values.companyData.city : values.invoiceData.invoiceCity}
            as={TextField}
            error={touched.invoiceData?.invoiceCity && errors.invoiceData?.invoiceCity}
            variant="outlined"
            size="small"
            fullWidth
            disabled={values.useSameAddress}
            required
          />
        </Grid>
        {touched.invoiceData?.invoiceCity && errors.invoiceData?.invoiceCity && (
          <ErrorMarker message={errors.invoiceData?.invoiceCity} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <FormControlLabel
            control={
              <Autocomplete
                className="register-form__field"
                options={countries as CountryType[]}
                autoHighlight
                fullWidth
                disabled={values.useSameAddress}
                value={
                  values.useSameAddress
                    ? countries.find(c => c.label === values.companyData.country)
                      ? countries.find(c => c.label === values.companyData.country)
                      : null
                    : countries.find(c => c.label === values.invoiceData.invoiceCountry)
                    ? countries.find(c => c.label === values.invoiceData.invoiceCountry)
                    : null
                }
                getOptionLabel={option => option.label}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ marginRight: '0.5em' }}>{countryToFlag(option.code)}</span>
                    {option.label} ({option.code})
                  </li>
                )}
                onChange={(event, value, reason) => {
                  setFieldValue('invoiceData.invoiceCountry', value?.label);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t(translations.common.country()) + '*'}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password'
                    }}
                    size="small"
                  />
                )}
              />
            }
            label={''}
            style={{ width: '100%' }}
          />
        </Grid>
        {touched.invoiceData?.invoiceCountry && errors.invoiceData?.invoiceCountry && (
          <ErrorMarker message={errors.invoiceData?.invoiceCountry} />
        )}
      </Grid>
      <Grid container className="register-form__info">
        * {t(translations.common.requiredFields())}
      </Grid>
    </>
  );
}
